/**
 * ficheCollab — les règles PURES de la fiche collaborateur fusionnée (07/09).
 *
 * Tout ce qui se décide sans réseau vit ici : la recherche entonnoir, le verdict
 * de signature, l'ancienneté, la taxonomie des documents. Aucune dépendance
 * React → testable à la main, et réutilisable par la liste comme par la fiche.
 *
 * ⚠️ Aucune de ces fonctions ne manipule de NIR : le verdict de clé est calculé
 * côté serveur (`nir_cle_valide` de /api/employees/{id}/full) et arrive ici en
 * booléen. Le masque d'affichage ne fait que cacher, il ne décide de rien.
 */

/* ── Recherche ENTONNOIR ───────────────────────────────────────────────────
   « propose et re-propose à CHAQUE lettre, nom ou prénom, même au milieu ».
   Donc : sans accent, sans casse, en SOUS-CHAÎNE (pas un préfixe), et chaque
   mot tapé doit se retrouver quelque part — « del jea » trouve Jean DELORME. */

/** Minuscule sans accents ni ponctuation parasite (NFD → on jette les diacritiques). */
export function sansAccents(s) {
  return String(s ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[’']/g, ' ')
    .replace(/[-_.]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Le texte cherchable d'un collaborateur : nom, prénom, poste, matricule. */
export function texteCherchable(c) {
  return sansAccents(`${c?.nom || ''} ${c?.prenom || ''} ${c?.poste || ''} ${c?.matricule || ''}`);
}

/**
 * Filtre entonnoir. Rend la liste ENTIÈRE quand la requête est vide (le champ
 * propose dès le premier caractère, pas après trois : c'est le sens de
 * « re-propose à chaque lettre »).
 *
 * @param {Array} liste  collaborateurs {id, nom, prenom, poste, matricule}
 * @param {string} q     ce qui est tapé
 * @param {number} max   nombre de suggestions rendues (0 = pas de limite)
 */
export function filtrerCollaborateurs(liste, q, max = 8) {
  const source = Array.isArray(liste) ? liste : [];
  const requete = sansAccents(q);
  if (!requete) return max ? source.slice(0, max) : source;
  const mots = requete.split(' ').filter(Boolean);
  const trouves = source.filter((c) => {
    const hay = texteCherchable(c);
    return mots.every((m) => hay.includes(m));
  });
  // Un nom qui COMMENCE par ce qui est tapé passe devant un nom qui le contient
  // au milieu — sans jamais l'exclure (« bonnet » doit rendre BONNET ARNOL).
  const score = (c) => {
    const nom = sansAccents(c?.nom);
    const prenom = sansAccents(c?.prenom);
    if (nom.startsWith(mots[0]) || prenom.startsWith(mots[0])) return 0;
    return 1;
  };
  trouves.sort((a, b) => score(a) - score(b)
    || sansAccents(a?.nom).localeCompare(sansAccents(b?.nom)));
  return max ? trouves.slice(0, max) : trouves;
}

/* ── Doctrine de signature (07/09, corrigée le 17/09) ──────────────────────
   QUATRE états depuis le 17/09. Le serveur tranche (`etat_signature`) ; ici on
   ne fait que l'HABILLER. Le repli local existe pour ne pas afficher « non
   signé » sur un contrat servi par une version d'API qui ne portait pas encore
   le champ — un faux rouge sur un contrat signé est le pire des mensonges
   d'écran.

   ⚠️ 17/09 — LE QUATRIÈME ÉTAT. « Signé papier » était posé sur la SEULE
   présence d'une pièce rattachée, sans avoir rien lu dedans : sur les 1 019
   contrats ainsi marqués, 34 PDF ne portent AUCUNE signature (le « Lu et
   approuvé » vient du gabarit). Le serveur les distingue désormais
   (`utils/signature_reelle`) et envoie `sans_signature` : la pièce reste
   consultable, mais le contrat est bien à faire signer. */

export const SIGNE_ELEC = 'electronique';
export const SIGNE_PAPIER = 'papier';
export const NON_SIGNE = 'absent';
/** Une pièce est rattachée, elle a été LUE, elle ne porte aucune signature. */
export const PIECE_SANS_SIGNATURE = 'piece_sans_signature';

/** Verdict d'un contrat : le champ serveur d'abord, repli sur les indices connus. */
export function etatSignature(contrat) {
  const serveur = contrat?.etat_signature;
  // `sans_signature` : le mot BRUT du lecteur de pièces
  // (backend/utils/signature_reelle.SANS) — même verdict, même habillage.
  if (serveur === 'sans_signature') return PIECE_SANS_SIGNATURE;
  if (serveur === SIGNE_ELEC || serveur === SIGNE_PAPIER || serveur === NON_SIGNE
      || serveur === PIECE_SANS_SIGNATURE) {
    return serveur;
  }
  // Repli : API ancienne. `statut === 'signe'` est posé par /full quand le
  // contrat porte `signed`. Une pièce rattachée = signé papier.
  if (String(contrat?.statut || '').toLowerCase().startsWith('sign')) return SIGNE_ELEC;
  if (contrat?.a_piece) return SIGNE_PAPIER;
  // Sans le champ ET sans indice : on ne sait pas. On ne crie pas au rouge.
  return serveur === undefined ? null : NON_SIGNE;
}

/**
 * Habillage du verdict — idiome badge maison (couleur texte, `40` bordure,
 * `10` fond), sauf le NON SIGNÉ qui est PLEIN : c'est le seul qui appelle un geste.
 */
export function badgeSignature(etat) {
  if (etat === SIGNE_ELEC) {
    return {
      label: 'Signé élec.',
      style: { color: '#10B981', borderColor: '#10B98140', background: '#10B98110' },
      titre: 'Signature électronique aboutie (circuit de signature ou PDF signé)',
    };
  }
  if (etat === SIGNE_PAPIER) {
    return {
      label: 'Signé papier',
      style: {
        color: '#10B981', borderColor: '#10B981', borderStyle: 'dashed', background: '#10B98108',
      },
      titre: "Pièce signée importée (contrat retrouvé) — jamais compté « non signé »",
    };
  }
  if (etat === PIECE_SANS_SIGNATURE) {
    return {
      label: 'Pièce non signée',
      style: { color: '#F59E0B', borderColor: '#F59E0B', background: '#F59E0B14' },
      titre: "Pièce rattachée et LUE : elle ne porte aucune signature — le contrat reste à faire signer",
    };
  }
  if (etat === NON_SIGNE) {
    return {
      label: 'Non signé',
      style: { color: '#fff', borderColor: '#F43F5E', background: '#F43F5E' },
      titre: 'Aucune signature électronique et aucune pièce rattachée',
    };
  }
  return null; // état inconnu → aucun badge, jamais un faux verdict
}

/** Un avenant se signale en violet (DA : #8B5CF6), quel que soit son état de signature. */
export const STYLE_AVENANT = {
  color: '#8B5CF6', borderColor: '#8B5CF640', background: '#8B5CF610',
};

/* ── Dates & ancienneté ────────────────────────────────────────────────────── */

/** "YYYY-MM-DD" ou "DD/MM/YYYY" → Date locale, sinon null. */
export function parseDateFr(s) {
  if (!s) return null;
  const str = String(s).slice(0, 10);
  let m = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  m = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return null;
}

/**
 * Ancienneté lisible depuis une date d'embauche : « 1 an 6 mois », « 4 mois ».
 * Rend '' quand la date est absente ou dans le futur — on n'invente pas une
 * ancienneté négative.
 */
export function anciennete(dateEmbauche, maintenant = new Date()) {
  const d = parseDateFr(dateEmbauche);
  if (!d || Number.isNaN(d.getTime()) || d > maintenant) return '';
  let mois = (maintenant.getFullYear() - d.getFullYear()) * 12
    + (maintenant.getMonth() - d.getMonth());
  if (maintenant.getDate() < d.getDate()) mois -= 1;
  if (mois < 0) return '';
  const ans = Math.floor(mois / 12);
  const reste = mois % 12;
  if (!ans) return `${reste} mois`;
  const partAns = `${ans} an${ans > 1 ? 's' : ''}`;
  return reste ? `${partAns} ${reste} mois` : partAns;
}

/** Initiales d'un collaborateur pour l'avatar du bandeau. */
export function initiales(prenom, nom) {
  const a = String(prenom || '').trim().charAt(0);
  const b = String(nom || '').trim().charAt(0);
  return `${a}${b}`.toUpperCase() || '?';
}

/* ── Taxonomie des documents ───────────────────────────────────────────────
   Miroir FIDÈLE de backend/routes/documents.py:OFFICIAL_DOC_CATEGORIES après la
   taxonomie tranchée le 07/09 (scripts/reclasser_documents_07_09.py). Les 10
   catégories créées ce jour-là DOIVENT figurer dans le sélecteur du dépôt :
   sans elles, une pièce déposée depuis le Bureau ne peut pas être rangée là où
   les 1 752 documents reclassés vivent désormais. */
export const CATEGORIES_DOCUMENTS = [
  // le dossier agent (les cases de la fiche)
  "Carte d'identite",
  'Carte vitale',
  'Carte professionnelle',
  'ICAD',
  "Photo d'identite",
  // les 10 créées le 07/09
  'RIB',
  'SST / Secourisme',
  'Titre de séjour',
  'Équipe cynophile',
  // 01/10/2026 — le vaccin du chien, exigé des cynophiles par le filet
  // « fiche incomplète » (backend/routes/documents.py, même libellé exact)
  'Vaccination (chien)',
  'SSIAP',
  'Casier judiciaire B3',
  'Visite médicale',
  'Justificatif de domicile',
  'Attestation de formation',
  'Permis de conduire',
  // le reste du référentiel officiel
  'Diplome',
  'Certificat médical',
  'Attestation',
  'Contrat',
  'DPAE',
  'Solde de tout compte (paie)',
  'Autre',
];

/** Libellé affichable d'une catégorie (les libellés sans accent restent lisibles). */
const LIBELLES = {
  "Carte d'identite": "Carte d'identité",
  "Photo d'identite": "Photo d'identité",
  Diplome: 'Diplôme',
  'Certificat medical': 'Certificat médical',
};
export function libelleCategorie(cat) {
  const c = String(cat || '').trim();
  return LIBELLES[c] || c || 'Sans catégorie';
}
